import {
  useCallback, useEffect, useReducer, useRef,
} from 'react';
import { captureAnalytics } from '../../../../actions/preload';
import { findSenderContacts } from '../../../../actions/senderContacts';
import { validFaxNumber, formatValidFaxNumber } from '../../../../global/validation';

export const normalizedFax = (value) => String(value || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
const mergeForm = (state, update) => ({ ...state, ...update });
const initialForm = (number) => ({
  contact: null,
  office: '',
  person: '',
  fax: formatValidFaxNumber(number),
  candidates: [],
  picking: false,
  loading: true,
  loadFailed: false,
  attempt: 0,
  conflictNumber: null,
  saving: false,
  error: '',
  confirmed: false,
});

const useSenderContactForm = ({
  record, onSave, onSaved, surface,
}) => {
  const sourceNumber = record.sender_email;
  const contactId = record.sender_contact_details.contact?.id;
  const [form, update] = useReducer(mergeForm, sourceNumber, initialForm);
  const alive = useRef(true);
  const savingRequest = useRef(false);
  const { attempt, conflictNumber } = form;

  const selectContact = useCallback((contact) => update({
    contact,
    office: contact?.organization_name || '',
    person: contact?.contact_name || '',
    fax: formatValidFaxNumber(contact?.contact || sourceNumber),
    picking: false,
    confirmed: false,
    error: '',
  }), [sourceNumber]);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    update({ loading: true, loadFailed: false });
    findSenderContacts({ fax_number: conflictNumber || sourceNumber, contact_id: conflictNumber ? undefined : contactId }, controller.signal).then((result) => {
      if (!result || controller.signal.aborted) return;
      if (!result.can_edit) { update({ loadFailed: true, error: 'Only admins can edit contacts.' }); return; }
      update({ candidates: result.contacts, error: '' });
      const linked = result.contacts.find((item) => item.id === contactId);
      if (conflictNumber) update({ picking: true });
      else if (linked) selectContact(linked);
      else if (result.contacts.length === 1) selectContact(result.contacts[0]);
      else update({ picking: result.contacts.length > 1 });
    }).catch((error) => {
      if (!controller.signal.aborted) update({ loadFailed: true, error: error.message });
    }).finally(() => {
      if (!controller.signal.aborted) update({ loading: false });
    });
    return () => controller.abort();
  }, [sourceNumber, contactId, attempt, conflictNumber, selectContact]);

  const numberChanged = Boolean(form.contact && normalizedFax(form.contact.contact) !== normalizedFax(form.fax));
  const save = async () => {
    if (savingRequest.current || form.loading || form.loadFailed) return;
    if ((!form.office.trim() && !form.person.trim()) || !validFaxNumber(form.fax).valid) {
      update({ error: 'Enter a valid fax number and an office or contact name.' });
      return;
    }
    if (numberChanged && !form.confirmed) { update({ error: 'Confirm the change to the saved fax number.' }); return; }
    savingRequest.current = true;
    update({ saving: true, error: '' });
    try {
      const result = await onSave({ organization_name: form.office.trim(), contact_name: form.person.trim(), contact: form.fax }, form.contact?.id, record.sender_contact_details.source_fax_id);
      if (!alive.current) return;
      if (result.error) {
        update({ error: result.response?.data?.error || 'Could not save contact. Please try again.' });
        captureAnalytics('sender_contact_save_failed', { surface });
        if (result.response?.status === 409 && !form.contact) update({ picking: true, conflictNumber: form.fax, attempt: attempt + 1 });
        return;
      }
      captureAnalytics('sender_contact_saved', { surface, editing: Boolean(form.contact), fax_changed: numberChanged });
      onSaved(result.data.contact);
    } catch {
      if (alive.current) update({ error: 'Could not save contact. Please try again.' });
    } finally {
      savingRequest.current = false;
      if (alive.current) update({ saving: false });
    }
  };

  return {
    form, update, selectContact, save, numberChanged,
  };
};

export default useSenderContactForm;
